import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl, downloadFile, formatApiErrorDetail, formatUploadError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MeetingDeleteConfirmation } from "@/components/MeetingDeleteConfirmation";
import MeetingEditDialog from "@/components/MeetingEditDialog";
import AnalysisExportButton from "@/components/AnalysisExportButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Mic, FileText, Upload, Sparkles, Download, Calendar, Building2,
  History, FileAudio, Wand2, ListChecks, CheckCircle2, Clock, User, Trash2, Users, Quote, Play,
} from "lucide-react";
import { toast } from "sonner";

const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon size={16} className="text-primary" strokeWidth={2} />
      <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">{children}</h3>
    </div>
    {right}
  </div>
);

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedRecording, setSelectedRecording] = useState("");
  const [selectedModel, setSelectedModel] = useState("gpt-6-luna");
  const [analysisSource, setAnalysisSource] = useState("transcript");
  const [transcribing, setTranscribing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summarizingSource, setSummarizingSource] = useState("");
  const [summarizingSpeakers, setSummarizingSpeakers] = useState(false);
  const [uploadingRec, setUploadingRec] = useState(false);
  const [uploadingMin, setUploadingMin] = useState(false);
  const [deletingFile, setDeletingFile] = useState("");
  const [deletingMeeting, setDeletingMeeting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const recInput = useRef();
  const minInput = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/meetings/${id}`);
      setMeeting(data);
      if (data.minutes_current?.storage_path && !data.transcript_current?.text) setAnalysisSource("minutes");
      if (data.transcript_current?.text && !data.minutes_current?.storage_path) setAnalysisSource("transcript");
      if (data.recordings?.length && !selectedRecording) {
        setSelectedRecording(data.recordings[0].id);
      }
    } catch {
      toast.error("Meeting not found");
      navigate("/");
    }
  };

  useEffect(() => {
    load();
    api.get("/models").then((r) => {
      const availableModels = r.data.models;
      setModels(availableModels);
      setSelectedModel((current) =>
        availableModels.some((model) => model.id === current)
          ? current
          : availableModels[0]?.id || current
      );
    });
    // eslint-disable-next-line
  }, [id]);

  const uploadRecording = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRec(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/meetings/${id}/recordings`, fd);
      setMeeting(data);
      if (!selectedRecording && data.recordings.length) setSelectedRecording(data.recordings[data.recordings.length - 1].id);
      toast.success("Recording uploaded");
    } catch (err) {
      toast.error(formatUploadError(err));
    } finally {
      setUploadingRec(false);
      if (recInput.current) recInput.current.value = "";
    }
  };

  const uploadMinutes = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMin(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/meetings/${id}/minutes`, fd);
      setMeeting(data);
      if (!data.transcript_current?.text) setAnalysisSource("minutes");
      toast.success(meeting?.minutes_current ? "New version saved" : "Minutes uploaded");
    } catch (err) {
      toast.error(formatUploadError(err));
    } finally {
      setUploadingMin(false);
      if (minInput.current) minInput.current.value = "";
    }
  };

  const runTranscribe = async () => {
    if (!selectedRecording) {
      toast.error("Select a recording first");
      return;
    }
    setTranscribing(true);
    try {
      const { data } = await api.post(`/meetings/${id}/transcribe`, { recording_id: selectedRecording });
      setMeeting(data);
      toast.success("Transcript generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTranscribing(false);
    }
  };

  const runSummarize = async (source) => {
    setAnalysisSource(source);
    setSummarizingSource(source);
    setSummarizing(true);
    try {
      const { data } = await api.post(`/meetings/${id}/summarize`, { model: selectedModel, source });
      setMeeting(data);
      toast.success("AI summary generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSummarizing(false);
      setSummarizingSource("");
    }
  };

  const runSpeakerSummary = async () => {
    setSummarizingSpeakers(true);
    try {
      const { data } = await api.post(`/meetings/${id}/speaker-summary`, { model: selectedModel, source: analysisSource });
      setMeeting(data);
      toast.success("Ringkasan per pembicara dibuat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSummarizingSpeakers(false);
    }
  };

  const deleteMeeting = async () => {
    setDeletingMeeting(true);
    try {
      await api.delete(`/meetings/${id}`);
      toast.success("Meeting dan seluruh file terkait dihapus permanen");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      await load();
    } finally {
      setDeletingMeeting(false);
    }
  };

  const deleteRecording = async (recording) => {
    setDeletingFile(`recording:${recording.id}`);
    try {
      const { data } = await api.delete(`/meetings/${id}/recordings/${recording.id}`);
      setMeeting(data);
      setSelectedRecording((current) => current === recording.id ? data.recordings?.[0]?.id || "" : current);
      if (!data.transcript_current?.text && data.minutes_current?.storage_path) setAnalysisSource("minutes");
      toast.success("Recording dihapus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletingFile("");
    }
  };

  const deleteMinutes = async (minutes) => {
    setDeletingFile(`minutes:${minutes.id}`);
    try {
      const { data } = await api.delete(`/meetings/${id}/minutes/${minutes.id}`);
      setMeeting(data);
      if (!data.minutes_current?.storage_path && data.transcript_current?.text) setAnalysisSource("transcript");
      toast.success("Manual Minutes dihapus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletingFile("");
    }
  };

  const doDownload = async (path, name) => {
    try {
      await downloadFile(path, name);
    } catch {
      toast.error("Gagal mengunduh file");
    }
  };

  const confirmDelete = () => {
    if (deleteTarget?.type === "meeting") deleteMeeting();
    if (deleteTarget?.type === "recording") deleteRecording(deleteTarget.file);
    if (deleteTarget?.type === "minutes") deleteMinutes(deleteTarget.file);
  };

  const deleteDialog = (
    <MeetingDeleteConfirmation
      target={deleteTarget}
      onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      onConfirm={confirmDelete}
    />
  );

  if (!meeting)
    return (
      <Layout>
        <div className="p-12 text-muted-foreground" data-testid="detail-loading">Loading…</div>
      </Layout>
    );

  const t = meeting.transcript_current;
  const s = meeting.summary;
  const sp = meeting.speaker_summary;
  const canManage = meeting.created_by === user?.id || (user?.role === "member" && user?.department && meeting.department === user.department);
  const hasTranscript = Boolean(t?.text?.trim());
  const hasManualMinutes = Boolean(meeting.minutes_current?.storage_path);
  const hasAnalysisSource = analysisSource === "minutes" ? hasManualMinutes : hasTranscript;

  if (meeting.deletion_pending) return (
    <Layout>
      {deleteDialog}
      <div className="p-4 sm:p-8 lg:p-12 max-w-2xl">
        <h1 className="font-heading font-black text-3xl tracking-tight mb-4">Penghapusan meeting tertunda</h1>
        <p className="text-sm text-muted-foreground mb-6">Meeting “{meeting.title}” sedang menunggu pembersihan file. Coba lanjutkan penghapusan. Perubahan dan upload baru ke meeting ini sementara diblokir.</p>
        <div className="flex gap-3 flex-wrap">
          {canManage && <Button onClick={() => setDeleteTarget({ type: "meeting" })} disabled={deletingMeeting} className="rounded-none">{deletingMeeting ? "Menghapus..." : "Coba hapus lagi"}</Button>}
          <Button variant="outline" onClick={() => navigate("/")} className="rounded-none">Kembali ke Meeting History</Button>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      {deleteDialog}
      <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]">
        <button
          onClick={() => navigate("/")}
          data-testid="detail-back-btn"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-6"
        >
          <ArrowLeft size={16} /> Back to History
        </button>

        {/* Header */}
        <div className="flex flex-col gap-4 border-b border-border pb-6 mb-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight break-words" data-testid="meeting-title">
              {meeting.title}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1.5 mono"><Calendar size={14} /> {meeting.date}</span>
              <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {meeting.department}</span>
              {user?.role === "admin" && <span className="inline-flex items-center gap-1.5"><User size={14} /> {meeting.created_by_name || "Unknown creator"}</span>}
            </div>
            {!canManage && <p className="text-sm text-muted-foreground mt-3" data-testid="meeting-read-only">File dan hasil analisis dapat dilihat. Admin dapat memperbarui metadata meeting.</p>}
          </div>
          {(canManage || user?.role === "admin") && (
            <div role="group" aria-label="Aksi meeting" className={`grid w-full gap-2 sm:w-auto sm:shrink-0 sm:pt-1 ${canManage ? "grid-cols-2" : "grid-cols-1"}`}>
              <MeetingEditDialog meeting={meeting} user={user} onSaved={setMeeting} />
              {canManage && <Button
                variant="outline"
                onClick={() => setDeleteTarget({ type: "meeting" })}
                disabled={deletingMeeting}
                data-testid="delete-meeting-btn"
                className="h-9 w-full rounded-none border-primary/25 px-3 text-primary shadow-none hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
              >
                <Trash2 size={16} /> Hapus Meeting
              </Button>}
            </div>
          )}
        </div>

        {/* Upload row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Recordings */}
          <div className="border border-border bg-card p-6">
            <SectionTitle icon={Mic}>Recordings</SectionTitle>
            {meeting.recordings?.length > 0 ? (
              <div className="space-y-2 mb-4">
                {meeting.recordings.map((r) => (
                  <RecordingPlayer
                    key={r.id}
                    rec={r}
                    onDelete={canManage ? () => setDeleteTarget({ type: "recording", file: r }) : null}
                    deleting={Boolean(deletingFile)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">No recordings uploaded yet.</p>
            )}
            {canManage && <>
            <input ref={recInput} type="file" accept=".mp3,.wav,.aac,.m4a,audio/*" onChange={uploadRecording} className="hidden" data-testid="recording-file-input" />
            <Button
              variant="outline"
              onClick={() => recInput.current?.click()}
              disabled={uploadingRec || Boolean(deletingFile)}
              data-testid="upload-recording-btn"
              className="rounded-none w-full border-foreground/20"
            >
              <Upload size={16} className="mr-2" /> {uploadingRec ? "Uploading…" : "Upload Recording (MP3/WAV/AAC)"}
            </Button>
            </>}
          </div>

          {/* Minutes */}
          <div className="border border-border bg-card p-6">
            <SectionTitle icon={FileText} right={
              meeting.minutes_versions?.length > 0 && (
                <VersionDialog
                  title="Minutes Version History"
                  items={meeting.minutes_versions}
                  render={(v) => (
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="rounded-none mono">v{v.version}</Badge>
                      <span className="text-sm flex-1 truncate">{v.original_filename}</span>
                      <span className="mono text-xs text-muted-foreground">{v.uploaded_at?.slice(0, 10)}</span>
                      <button onClick={() => doDownload(v.storage_path, v.original_filename)} className="text-muted-foreground hover:text-primary" data-testid={`download-minutes-version-${v.version}`} title="Unduh"><Download size={14} /></button>
                      {canManage && <button
                        onClick={() => setDeleteTarget({ type: "minutes", file: v })}
                        disabled={Boolean(deletingFile)}
                        className="text-muted-foreground hover:text-primary disabled:opacity-50"
                        data-testid={`delete-minutes-version-${v.id}`}
                        aria-label={`Hapus ${v.original_filename}`}
                        title="Hapus file"
                      ><Trash2 size={14} /></button>}
                    </div>
                  )}
                />
              )
            }>Manual Minutes</SectionTitle>
            {meeting.minutes_current ? (
              <div className="flex items-center gap-3 border border-border px-3 py-2 mb-4" data-testid="current-minutes">
                <FileText size={16} className="text-muted-foreground shrink-0" />
                <span className="text-sm truncate flex-1">{meeting.minutes_current.original_filename}</span>
                <Badge variant="outline" className="rounded-none mono text-[10px]">v{(meeting.minutes_versions?.length || 0) + 1}</Badge>
                <button onClick={() => doDownload(meeting.minutes_current.storage_path, meeting.minutes_current.original_filename)} className="text-muted-foreground hover:text-primary" data-testid="download-minutes" title="Unduh">
                  <Download size={15} />
                </button>
                {canManage && <button
                  onClick={() => setDeleteTarget({ type: "minutes", file: meeting.minutes_current })}
                  disabled={Boolean(deletingFile)}
                  className="text-muted-foreground hover:text-primary disabled:opacity-50"
                  data-testid="delete-current-minutes"
                  aria-label={`Hapus ${meeting.minutes_current.original_filename}`}
                  title="Hapus file"
                ><Trash2 size={15} /></button>}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">No minutes uploaded yet.</p>
            )}
            {canManage && <>
            <input ref={minInput} type="file" accept=".pdf,.docx,.doc" onChange={uploadMinutes} className="hidden" data-testid="minutes-file-input" />
            <Button
              variant="outline"
              onClick={() => minInput.current?.click()}
              disabled={uploadingMin || Boolean(deletingFile)}
              data-testid="upload-minutes-btn"
              className="rounded-none w-full border-foreground/20"
            >
              <Upload size={16} className="mr-2" /> {uploadingMin ? "Uploading…" : meeting.minutes_current ? "Upload New Version" : "Upload Minutes (PDF/DOCX)"}
            </Button>
            </>}
          </div>
        </div>

        {/* Main 2-column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Transcript (left, 2/3) */}
          <div className="lg:col-span-2 border border-border bg-card p-6">
            <SectionTitle icon={FileAudio} right={
              t && meeting.transcript_versions?.length > 0 && (
                <VersionDialog
                  title="Transcript Version History"
                  items={meeting.transcript_versions}
                  render={(v) => (
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Badge variant="outline" className="rounded-none mono">v{v.version}</Badge>
                        <span className="mono text-xs text-muted-foreground">{v.created_at?.slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto">{v.text}</p>
                    </div>
                  )}
                />
              )
            }>Transcript</SectionTitle>

            {canManage && <div className="flex flex-col sm:flex-row gap-3 mb-5 pb-5 border-b border-border">
              <Select value={selectedRecording} onValueChange={setSelectedRecording}>
                <SelectTrigger data-testid="transcribe-recording-select" className="rounded-none flex-1">
                  <SelectValue placeholder="Select a recording to transcribe" />
                </SelectTrigger>
                <SelectContent>
                  {meeting.recordings?.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.original_filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={runTranscribe}
                disabled={transcribing || !meeting.recordings?.length}
                data-testid="generate-transcript-btn"
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Wand2 size={16} className="mr-2" /> {transcribing ? "Transcribing…" : "Generate Transcript"}
              </Button>
            </div>}

            {t ? (
              <div data-testid="transcript-content">
                <div className="mono text-xs text-muted-foreground mb-4">
                  Source: {t.source_filename} · {t.created_at?.slice(0, 16).replace("T", " ")}
                </div>
                <div className="space-y-4 max-w-2xl">
                  {t.text.split(/\n\n+/).map((para, i) => (
                    <p key={i} className="text-[15px] leading-7 text-foreground/90">{para}</p>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-16 text-center" data-testid="transcript-empty">
                <FileAudio size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground text-sm">
                  {!canManage ? "No transcript generated yet." : meeting.recordings?.length
                    ? "Select a recording and generate a transcript."
                    : "Upload a recording to enable transcription."}
                </p>
              </div>
            )}
          </div>

          {/* AI Summary (right, 1/3, sticky) */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8 border border-border bg-card p-6">
              <SectionTitle icon={Sparkles}>AI Analysis</SectionTitle>
              {(s || sp) && <div className="mb-4"><AnalysisExportButton meetingId={id} kind="pdf" /></div>}

              {canManage && <>
              <div className="space-y-3 mb-5 pb-5 border-b border-border">
                <label className="block text-xs text-muted-foreground" htmlFor="analysis-model">Model AI</label>
                <Select value={selectedModel} onValueChange={setSelectedModel} disabled={!models.length || summarizing || summarizingSpeakers}>
                  <SelectTrigger id="analysis-model" aria-label="Model AI" data-testid="model-select" className="rounded-none flex-1 text-xs">
                    <SelectValue placeholder="Pilih model AI" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasTranscript && <Button
                onClick={() => runSummarize("transcript")}
                disabled={summarizing || summarizingSpeakers}
                data-testid="generate-summary-btn"
                className="rounded-none w-full bg-primary hover:bg-primary/90 text-primary-foreground mb-3"
              >
                <Sparkles size={16} className="mr-2" /> {summarizingSource === "transcript" ? "Menganalisis…" : "Analyze Transcript"}
              </Button>}

              {hasManualMinutes && <Button
                onClick={() => runSummarize("minutes")}
                disabled={summarizing || summarizingSpeakers}
                data-testid="generate-minutes-summary-btn"
                className="rounded-none w-full bg-primary hover:bg-primary/90 text-primary-foreground mb-5"
              >
                <FileText size={16} className="mr-2" /> {summarizingSource === "minutes" ? "Menganalisis…" : "Analyze Manual Minutes"}
              </Button>}

              {(hasTranscript || hasManualMinutes) && <>
                {hasTranscript && hasManualMinutes && <div className="mb-3">
                  <label className="block text-xs text-muted-foreground mb-1" htmlFor="speaker-analysis-source">Sumber ringkasan per pembicara</label>
                  <Select value={analysisSource} onValueChange={setAnalysisSource}>
                    <SelectTrigger id="speaker-analysis-source" data-testid="speaker-analysis-source-select" className="rounded-none w-full text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transcript">Generated Transcript</SelectItem>
                      <SelectItem value="minutes">Manual Minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>}

                <Button
                  variant="outline"
                  onClick={runSpeakerSummary}
                  disabled={summarizingSpeakers || summarizing || !hasAnalysisSource}
                  data-testid="generate-speaker-summary-btn"
                  className="rounded-none w-full border-foreground/20 mb-5"
                >
                  <Users size={16} className="mr-2" /> {summarizingSpeakers ? "Menganalisis…" : "Ringkasan per Pembicara"}
                </Button>
              </>}

              {!hasTranscript && !hasManualMinutes && <p className="text-xs text-muted-foreground" data-testid="summary-need-source">
                Generate a transcript or upload Manual Minutes to enable AI analysis.
              </p>}
              </>}
              {!canManage && !s && !sp && <p className="text-sm text-muted-foreground">No AI analysis generated yet.</p>}

              {s && (
                <div className="space-y-6" data-testid="summary-content">
                  <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {s.model_label} · {s.generated_at?.slice(0, 10)}
                  </div>
                  <div className="text-xs text-muted-foreground" data-testid="summary-source">
                    Source: {s.source === "minutes" ? `Manual Minutes${s.source_filename ? ` (${s.source_filename})` : ""}` : "Generated Transcript"}
                  </div>

                  <div>
                    <h4 className="font-heading font-bold text-xs tracking-[0.15em] uppercase mb-2 text-muted-foreground">Overview</h4>
                    <p className="text-sm leading-relaxed" data-testid="summary-overview">{s.overview}</p>
                  </div>

                  <div>
                    <h4 className="font-heading font-bold text-xs tracking-[0.15em] uppercase mb-2 text-muted-foreground flex items-center gap-1.5">
                      <ListChecks size={13} /> Key Decisions
                    </h4>
                    <ul className="space-y-2" data-testid="key-decisions">
                      {(s.key_decisions || []).map((d, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-primary font-bold mono">{String(i + 1).padStart(2, "0")}</span>
                          <span>{d}</span>
                        </li>
                      ))}
                      {(!s.key_decisions || s.key_decisions.length === 0) && (
                        <li className="text-sm text-muted-foreground">None recorded.</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Items table (full width) */}
        {s && (
          <div className="border border-border bg-card p-6 mt-6" data-testid="action-items-section">
            <SectionTitle icon={CheckCircle2} right={<AnalysisExportButton meetingId={id} kind="xlsx" />}>Action Items</SectionTitle>
            {s.action_items?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-foreground/80 text-left">
                      <th className="py-3 pr-4 font-heading font-bold text-xs tracking-[0.1em] uppercase text-muted-foreground">Task</th>
                      <th className="py-3 pr-4 font-heading font-bold text-xs tracking-[0.1em] uppercase text-muted-foreground w-40"><span className="inline-flex items-center gap-1"><User size={12} /> PIC</span></th>
                      <th className="py-3 font-heading font-bold text-xs tracking-[0.1em] uppercase text-muted-foreground w-40"><span className="inline-flex items-center gap-1"><Clock size={12} /> Deadline</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.action_items.map((a, i) => (
                      <tr key={i} className="border-b border-border" data-testid={`action-item-${i}`}>
                        <td className="py-3 pr-4">{a.task}</td>
                        <td className="py-3 pr-4">{a.pic}</td>
                        <td className="py-3">
                          <span className={`mono text-xs px-2 py-1 border ${a.deadline && a.deadline !== "TBD" ? "border-primary/50 text-primary" : "border-border text-muted-foreground"}`}>
                            {a.deadline || "TBD"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No action items identified.</p>
            )}
          </div>
        )}

        {/* Per-speaker summary (full width) */}
        {sp && (
          <div className="border border-border bg-card p-6 mt-6" data-testid="speaker-summary-section">
            <SectionTitle icon={Users} right={
              <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {sp.model_label} · {sp.generated_at?.slice(0, 10)}
              </span>
            }>Ringkasan per Pembicara</SectionTitle>
            <p className="text-xs text-muted-foreground mb-4" data-testid="speaker-summary-source">
              Source: {sp.source === "minutes" ? `Manual Minutes${sp.source_filename ? ` (${sp.source_filename})` : ""}` : "Generated Transcript"}
            </p>
            {sp.speakers?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sp.speakers.map((p, i) => (
                  <div key={i} className="border border-border p-4" data-testid={`speaker-${i}`}>
                    <div className="flex items-center gap-3 mb-3 pb-3 border-b border-border">
                      <div className="h-9 w-9 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <User size={18} strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-base tracking-tight truncate">{p.name}</div>
                        {p.role && p.role !== "-" && (
                          <div className="text-xs text-muted-foreground truncate">{p.role}</div>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {(p.views || []).map((v, j) => (
                        <li key={j} className="text-sm flex gap-2 leading-relaxed">
                          <Quote size={13} className="text-primary shrink-0 mt-1" strokeWidth={2} />
                          <span>{v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Tidak ada pembicara yang teridentifikasi menyampaikan pandangan.</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

const RecordingPlayer = ({ rec, onDelete, deleting }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [src]);

  const loadAudio = async () => {
    if (src || loading) return;
    setLoading(true);
    try {
      const res = await api.get(`/files/${rec.storage_path}`, { responseType: "blob" });
      setSrc(URL.createObjectURL(res.data));
    } catch {
      toast.error("Gagal memuat audio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border px-3 py-2" data-testid={`recording-${rec.id}`}>
      <div className="flex items-center gap-3">
        <FileAudio size={16} className="text-muted-foreground shrink-0" />
        <span className="text-sm truncate flex-1">{rec.original_filename}</span>
        {!src && (
          <button
            onClick={loadAudio}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors duration-150"
            data-testid={`play-recording-${rec.id}`}
            title="Putar"
          >
            <Play size={15} /> {loading ? "Memuat…" : "Putar"}
          </button>
        )}
        {onDelete && <button
          onClick={onDelete}
          disabled={deleting}
          className="text-muted-foreground hover:text-primary disabled:opacity-50"
          data-testid={`delete-recording-${rec.id}`}
          aria-label={`Hapus ${rec.original_filename}`}
          title="Hapus file"
        ><Trash2 size={15} /></button>}
      </div>
      {src && (
        <audio
          src={src}
          controls
          autoPlay
          controlsList="nodownload noplaybackrate"
          onContextMenu={(e) => e.preventDefault()}
          className="w-full mt-3 h-9"
          data-testid={`audio-recording-${rec.id}`}
        />
      )}
    </div>
  );
};

const VersionDialog = ({ title, items, render }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors duration-150" data-testid="version-history-btn">
        <History size={14} /> History ({items.length})
      </button>
    </DialogTrigger>
    <DialogContent className="rounded-none max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-heading tracking-tight">{title}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-auto">
        {[...items].reverse().map((v, i) => (
          <div key={i} className="border border-border p-3">{render(v)}</div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
